import { create } from 'zustand';
import { Capacitor } from '@capacitor/core';
import { createJSONStorage, persist } from 'zustand/middleware';
import { secureZustandStorage } from '@/services/secure-storage.service';
import { useTenantStore } from './tenant.store';
import { useMessagingStore } from './messaging.store';
import { useNotificationStore } from './notification.store';
import { useFormsStore } from './forms.store';
import { useAiAssistantStore } from './ai-assistant.store';
import { queryClient } from '@/config/queryClient';
import { isEmbeddedMode } from '@/utils/embedded-console';

/**
 * On the native bridge the token is kept in the Keystore-backed vault
 * (services/auth-token-persistence.ts), never in the persisted blob, which
 * lands in localStorage and in @capacitor/preferences in plaintext (P1-12).
 */
function tokenLivesInVault(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

/**
 * Tell the service worker to evict the URL-keyed `/api` response cache.
 * Guarded for SSR / browsers without service-worker support and for the case
 * where no SW controls the page yet. Best-effort and non-throwing.
 */
function clearServiceWorkerApiCache(): void {
  try {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    navigator.serviceWorker.controller?.postMessage({ type: 'CLEAR_API_CACHE' });
  } catch {
    // Non-fatal — the SW also skips caching authenticated responses.
  }
}

/**
 * Decode a JWT payload without signature verification (client-side convenience
 * only — the backend is the authority). Returns null for malformed tokens.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // Base64url → Base64, then decode
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded && typeof decoded === 'object' ? decoded : null;
  } catch {
    return null;
  }
}

/**
 * Check if a JWT token is expired by decoding the payload and comparing `exp` to now.
 * Returns true if expired, malformed, or missing `exp`. Uses a 60-second buffer
 * so the app logs out slightly before the token actually expires.
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeJwtPayload(token);
  if (!decoded || typeof decoded.exp !== 'number') return true;
  // Expire 60 seconds early to avoid edge-case requests with almost-expired tokens
  return decoded.exp * 1000 <= Date.now() + 60_000;
}

/**
 * Reconcile the persisted active tenant against a freshly-issued JWT's
 * memberships.
 *
 * The tenant store persists `activeTenant` across sessions, but it is NOT
 * cleared on logout (so the same user re-logging-in keeps their selection).
 * That means a different user logging in on the same device — or the same
 * user whose membership in the selected tenant was revoked — would otherwise
 * keep sending an `X-Tenant-ID` header for a tenant that isn't in the new
 * token's memberships. The backend rejects every such request with
 * 401 INVALID_TOKEN_CLAIMS, which the api interceptor treats as an expired
 * session → immediate logout loop (GitHub issue #740).
 *
 * If the persisted tenant isn't in the new token's memberships, reset the
 * tenant store: with no active tenant the api client sends no X-Tenant-ID
 * header (the backend falls back to the token's default tenant), and
 * `setTenants()` auto-selects the first valid tenant once the list loads.
 *
 * Mirrors the backend exemptions in auth.middleware.ts: platform admins and
 * impersonation tokens may address any tenant, so their selection is kept.
 */
export function reconcileActiveTenantWithToken(token: string): void {
  const payload = decodeJwtPayload(token);
  if (!payload) return;
  if (payload.is_platform_admin === true || payload.impersonation === true) return;

  const { activeTenant, clearTenants } = useTenantStore.getState();
  if (!activeTenant) return;

  const memberships = Array.isArray(payload.memberships) ? payload.memberships : [];
  const stillMember = memberships.some(
    (m) => m && typeof m === 'object' && (m as { tenant_id?: string }).tenant_id === activeTenant.id
  );
  if (!stillMember) {
    clearTenants();
  }
}

/**
 * Would the backend accept an `X-Tenant-ID: tenantId` header for requests
 * authenticated with this token?
 *
 * Mirrors the header cross-check in backend auth.middleware.ts: the header
 * must match the token's `tenant_id` or one of its embedded memberships.
 * The check is skipped server-side for platform admins, impersonation
 * tokens, and tokens with no `tenant_id` claim (fresh users with no
 * memberships at sign-in) — those all return true here.
 *
 * Used by flows that gain a NEW membership mid-session (invitation
 * acceptance): the membership exists in the DB but not in the current JWT,
 * so selecting that tenant would 401 every request until re-login.
 */
export function tokenPermitsTenantHeader(token: string, tenantId: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return true; // can't tell client-side — let the backend decide
  if (payload.is_platform_admin === true || payload.impersonation === true) return true;
  if (!payload.tenant_id) return true; // backend skips the check for tokens without a tenant claim
  if (payload.tenant_id === tenantId) return true;
  const memberships = Array.isArray(payload.memberships) ? payload.memberships : [];
  return memberships.some(
    (m) => m && typeof m === 'object' && (m as { tenant_id?: string }).tenant_id === tenantId
  );
}

interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string;
  phone?: string;
  job_title?: string;
  onboarding_completed_at?: string | null;
  is_platform_admin?: boolean;
}

export interface Permission {
  module: string;
  action: string;
}

/**
 * Why a session ended. Only `'user'` means the person chose to sign out.
 * `'idle'` (tenant idle timeout) and `'expired'` (a 401, or an expired token
 * found at start-up) end the session without anyone asking for it.
 *
 * Native push depends on the difference (native-push.service.ts): an explicit
 * sign-out deletes this device's push token, while an automatic one keeps the
 * device registered so the same person still gets SOS and evacuation pushes
 * on a phone that timed out in their pocket.
 *
 * `'revoked'` means the server answered with a terminal auth code
 * (SESSION_REVOKED, MEMBERSHIP_REMOVED, USER_DISABLED, TOKEN_REVOKED): this
 * credential can never work again, so it is treated like an explicit sign-out
 * and the device's offline field data is wiped (services/terminal-auth.ts).
 */
export type LogoutReason = 'user' | 'idle' | 'expired' | 'revoked';

interface AuthState {
  user: User | null;
  token: string | null;
  /** Web console only (P4-07): rotating refresh token, see services/session-refresh.ts. */
  refreshToken: string | null;
  isAuthenticated: boolean;
  permissions: Permission[];
  /** Why the most recent logout() happened (not persisted). */
  lastLogoutReason: LogoutReason | null;

  setUser: (user: User) => void;
  /** A sign-in or refresh. `refreshToken` omitted = a session without refresh (it clears any old one). */
  setToken: (token: string, refreshToken?: string | null) => void;
  setPermissions: (permissions: Permission[]) => void;
  /** Required on purpose: every caller must say whether the person asked to sign out. */
  logout: (reason: LogoutReason) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      permissions: [],
      lastLogoutReason: null,

      setUser: (user) => set({ user, isAuthenticated: true }),

      setToken: (token, refreshToken) => {
        // Only the web console keeps a refresh token (P4-07). Native shells
        // renew through their device session and the embedded console through
        // its handoff: neither may hold a console refresh family, even if a
        // response carried one.
        const keepRefresh = !!refreshToken && !tokenLivesInVault() && !isEmbeddedMode();
        set({ token, refreshToken: keepRefresh ? refreshToken : null });
        // Every login path (password, magic link, MFA challenge, SSO callback)
        // funnels through setToken — validate the persisted active tenant
        // against the new token's memberships before any request goes out
        // with a stale X-Tenant-ID header.
        reconcileActiveTenantWithToken(token);
      },

      setPermissions: (permissions) => set({ permissions }),

      logout: (reason) => {
        // Clear tenant-scoped stores to prevent stale cross-tenant data.
        // clearAllState() on the messaging store also removes its raw
        // 'messaging-storage' localStorage key.
        useMessagingStore.getState().clearAllState();
        useNotificationStore.getState().clearAllState();
        useFormsStore.getState().clearAllState();
        useAiAssistantStore.getState().clearAllState();
        queryClient.clear();

        // Evict the service worker's URL-keyed /api cache so a second user on a
        // shared device can't be served the first user's cached API responses.
        clearServiceWorkerApiCache();

        // The reason travels in the same state change as the sign-out, so a
        // store subscriber (native push) sees both together.
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          permissions: [],
          lastLogoutReason: reason,
        });
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => secureZustandStorage),
      partialize: (state) => ({
        user: state.user,
        // Native: the token is in the Keystore vault, never in this blob.
        ...(tokenLivesInVault() ? {} : { token: state.token, refreshToken: state.refreshToken }),
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
      }),
      // Migrate persisted state from earlier versions that included the
      // `isAuth0` field. Auth0 was retired in Phase 6; the field is harmless
      // if left in storage, but we strip it on rehydrate so subsequent
      // writes don't carry it forward.
      migrate: (persistedState: unknown) => {
        if (persistedState && typeof persistedState === 'object') {
          const s = persistedState as Record<string, unknown>;
          delete s.isAuth0;
          return s;
        }
        return persistedState;
      },
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // If rehydrated state claims authenticated but the token is expired/missing, clear it
        // Native: the token is not in this blob; hydrateNativeAuthToken()
        // (main.tsx, before the first render) reads the vault and decides.
        if (state.isAuthenticated && !state.token && tokenLivesInVault()) return;
        // A refreshable console session outlives its 15-minute access token:
        // the API client renews it before the first request (P4-07).
        const refreshable = !!state.token && !!state.refreshToken;
        if (state.isAuthenticated && (!state.token || (isTokenExpired(state.token) && !refreshable))) {
          state.logout('expired');
        } else if (state.isAuthenticated && state.token) {
          // Heal sessions persisted before the setToken() reconciliation
          // existed: a stale activeTenant + valid token would otherwise 401
          // on every request. The tenant store rehydrates synchronously
          // before this runs (it's imported above and uses sync storage).
          reconcileActiveTenantWithToken(state.token);
        }
      },
    }
  )
);

/**
 * Check if the current user has a specific permission.
 * Platform admins bypass all permission checks.
 */
export function hasPermission(module: string, action: string): boolean {
  const state = useAuthStore.getState();
  if (state.user?.is_platform_admin) return true;
  return state.permissions.some(p => p.module === module && p.action === action);
}
